import formatDistanceToNowStrict from 'date-fns/formatDistanceToNowStrict';

import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { DateFormat, formatDateWithYear } from '@lyearn/core/utils/date/formatDate';
import { TFunction } from '@lyearn/i18n';
import {
  BriefcaseOutline,
  Calendar2Outline,
  ClockOutline,
  EmailOutline,
  PaperPlane2Fill,
  PersonOutline,
} from '@lyearn/icons';
import { LearnerProfileDetailsFragment } from '@/modules/learner/views/LearnerProfile/graphql/fragments/generated/LearnerProfileDetails';
import { shortenTimeUnitLabel } from '@/modules/report/views/ReportEditor/utils/formatters/metric';
import routes from '@/pages/corporate/routes';
import { Maybe, UserStatusEnum } from '@/types/schema';

interface Params {
  isAdmin: boolean;
  isSelf?: boolean;
  learner: LearnerProfileDetailsFragment;
  t: TFunction;
  isMobile?: boolean;
  isJoinedDateVisible?: boolean;
}

interface LearnerInfoItem {
  Icon: typeof PersonOutline;
  label?: Maybe<string>;
  to?: string;
}

export const getLearnerOrgRoleRoute = (
  orgRole: LearnerProfileDetailsFragment['orgRole'],
  isAdmin: boolean,
) => {
  if (!orgRole) {
    return undefined;
  }

  if (isAdmin) {
    return routes.OrgRoleDetails(orgRole.id);
  }

  return routes.LearnerOrgRoleDetails(orgRole.id);
};

export const getLearnerInfoItems = (params: Params): LearnerInfoItem[] => {
  const { isAdmin, isMobile, isSelf, learner, t, isJoinedDateVisible } = params;
  const { createdAt, department, orgRole, status, lastActiveAt } = learner;
  const isInvited = status === UserStatusEnum.Invited;

  return getEnabledItems([
    {
      Icon: EmailOutline,
      label: learner?.emailId,
      enabled: isMobile,
    },
    {
      Icon: PersonOutline,
      label: department?.name,
      enabled: Boolean(department),
    },
    {
      Icon: BriefcaseOutline,
      label: orgRole?.name,
      to: getLearnerOrgRoleRoute(orgRole, isAdmin),
      enabled: Boolean(orgRole),
    },
    {
      Icon: Calendar2Outline,
      label: t('LearnerProfile:Joined', {
        date: formatDateWithYear(createdAt, DateFormat.Date),
      }),
      enabled: Boolean(createdAt && !isInvited && isJoinedDateVisible),
    },
    {
      Icon: ClockOutline,
      label: lastActiveAt
        ? t('LearnerProfile:LastActive', {
            date: shortenTimeUnitLabel(formatDistanceToNowStrict(new Date(lastActiveAt))),
          })
        : '',
      enabled: Boolean(lastActiveAt) && !isSelf,
    },
    {
      Icon: PaperPlane2Fill,
      label: t('LearnerProfile:InvitedOn', {
        date: formatDateWithYear(createdAt, DateFormat.Date),
      }),
      enabled: Boolean(createdAt && isInvited),
    },
  ]);
};
